import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";

gsap.registerPlugin(ScrollTrigger);

// ✅ Optimized ImageKit URLs with auto-format and exact DPR bounds
const services = [
  {
    id: "ebay",
    slug: "ebay",
    name: "eBay UK",
    icon: "https://ik.imagekit.io/g9mnvfpan/ebay.jpg?tr=w-112,h-112,q-80,f-auto",
    tagline: "35M+ Active British Buyers",
    description:
      "We create and manage your eBay UK seller account with optimised listings, strategic category placement, and PowerSeller guidance.",
    image: "https://ik.imagekit.io/g9mnvfpan/gr1-removebg-preview.png?tr=w-800,q-80,f-auto",
  },
  {
    id: "shopify",
    slug: "shopify",
    name: "Shopify",
    icon: "https://ik.imagekit.io/g9mnvfpan/shopiffy.png?tr=w-112,h-112,q-80,f-auto",
    tagline: "Your Brand, Your Storefront",
    description:
      "Custom Shopify build from scratch — theme design, UK domain setup, local payment gateways, VAT configuration, and launch-ready performance.",
    image: "https://ik.imagekit.io/g9mnvfpan/gr2-removebg-preview.png?tr=w-800,q-80,f-auto",
  },
  {
    id: "tiktok",
    slug: "tiktok-shop",
    name: "TikTok Shop",
    icon: "https://ik.imagekit.io/g9mnvfpan/tiktok-icon2.png?tr=w-112,h-112,q-80,f-auto",
    tagline: "Go Viral & Generate Revenue",
    description:
      "Full TikTok Shop account creation, UK-optimised product listings, affiliate creator setup, and live shopping strategy.",
    image: "https://ik.imagekit.io/g9mnvfpan/gr1-removebg-preview.png?tr=w-800,q-80,f-auto",
  },
];

export default function ServicesShowcase() {
  const sectionRef = useRef(null);
  const cardsContainerRef = useRef(null);
  const textRef = useRef(null);
  const navigate = useNavigate();

  const handleReadMore = (slug) => navigate(`/services/${slug}`);

  useGSAP(() => {
    const section = sectionRef.current;
    const cards = cardsContainerRef.current;
    const text = textRef.current;
    if (!section || !cards || !text) return;

    // ✅ READ layout properties ONCE upfront to eliminate Forced Reflows
    const windowHeight = window.innerHeight;
    const cardsHeight = cards.offsetHeight;

    // ✅ Set hardware-accelerated initial transforms
    gsap.set(cards, { 
      y: windowHeight, 
      xPercent: -50,
      force3D: true 
    });

    // ✅ Smooth, native GSAP Timeline driven by ScrollTrigger (No manual style setting inside callbacks)
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top top",
        end: () => `+=${cardsHeight}`,
        pin: true,
        pinSpacing: true,
        scrub: 1,
        anticipatePin: 1,
        fastScrollEnd: true,
      },
    });

    // Animate Cards Container Y Translation
    tl.to(cards, {
      y: -cardsHeight,
      ease: "none",
    }, 0);

    // Animate Text Custom CSS Variable via GSAP Proxy
    tl.to(text, {
      "--scroll-ratio": "100%",
      ease: "none",
    }, 0);

  }, { scope: sectionRef });

  return (
    <>
      <style>{`
        .svc-section {
          position: relative;
          width: 100%;
          height: 100vh;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #ffffff;
          margin-top: clamp(60px, 8vw, 120px);
          margin-bottom: clamp(60px, 8vw, 120px);
        }

        .svc-bg-text-container {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 0;
          pointer-events: none;
          user-select: none;
          overflow: hidden;
          padding: 0;
        }

        .svc-bg-text {
          font-family: 'epilogue', sans-serif;
          font-size: clamp(50px, 18.5vw, 380px);
          font-weight: 900;
          line-height: 1;
          margin: 0;
          padding: 0;
          letter-spacing: -0.04em;
          text-align: center;
          width: 100%;
          white-space: nowrap;
          background: linear-gradient(
            to right,
            #f97316 var(--scroll-ratio, 0%),
            #e5e7eb var(--scroll-ratio, 0%)
          );
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .svc-cards-conveyor {
          position: absolute;
          top: 0;
          width: 100%;
          max-width: 580px;
          left: 50%;
          z-index: 1;
          will-change: transform;
          display: flex;
          flex-direction: column;
          gap: clamp(60px, 10vh, 120px);
          padding: 0 20px;
          box-sizing: border-box;
        }

        .svc-card-wrapper {
          perspective: 1500px;
          height: clamp(360px, 50vh, 440px);
          width: 100%;
        }

        .svc-card-inner {
          position: relative;
          width: 100%;
          height: 100%;
          transform-style: preserve-3d;
          transition: transform 0.6s cubic-bezier(0.25, 1, 0.5, 1);
          will-change: transform;
        }

        .svc-card-wrapper:hover .svc-card-inner {
          transform: rotateY(180deg);
        }

        .svc-card-front, .svc-card-back {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
          border-radius: 24px;
          overflow: hidden;
          border: 1px solid rgba(249, 115, 22, 0.15);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.05);
        }

        .svc-card-front {
          background: #ffffff;
          z-index: 2;
        }

        .svc-card-bg {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
        }

        .svc-card-glass {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            135deg,
            rgba(255, 255, 255, 0.96) 0%,
            rgba(255, 250, 245, 0.88) 60%,
            rgba(249, 115, 22, 0.04) 100%
          );
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
        }

        .svc-card-content {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: clamp(24px, 5vw, 40px);
          z-index: 2;
        }

        .svc-card-icon-wrapper {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: clamp(12px, 2.5vw, 20px);
        }

        .svc-card-icon {
          width: clamp(44px, 6vw, 56px);
          height: clamp(44px, 6vw, 56px);
          border-radius: 14px;
          object-fit: contain;
        }

        .svc-card-name {
          font-family: 'epilogue', sans-serif;
          font-size: clamp(22px, 4vw, 28px);
          font-weight: 700;
          color: #1a1a1a;
          margin: 0;
        }

        .svc-card-tagline {
          font-family: 'Inter', sans-serif;
          font-size: clamp(10px, 1.5vw, 11px);
          font-weight: 600;
          color: #f97316;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          margin-bottom: 12px;
          display: block;
        }

        .svc-card-desc {
          font-family: 'Inter', sans-serif;
          font-size: clamp(13.5px, 2vw, 14.5px);
          color: #555555;
          line-height: 1.6;
          margin: 0;
        }

        .svc-card-divider {
          width: 40px;
          height: 2px;
          margin-bottom: 14px;
        }

        .svc-card-back {
          background: #1a1a1a;
          transform: rotateY(180deg);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: clamp(20px, 5vw, 40px);
          text-align: center;
          z-index: 3;
        }

        .svc-back-title {
          font-family: 'epilogue', sans-serif;
          font-size: clamp(24px, 4vw, 32px);
          color: #ffffff;
          margin-bottom: 8px;
        }

        .svc-back-tagline {
          font-family: 'Inter', sans-serif;
          font-size: clamp(11px, 1.8vw, 13px);
          color: #f97316;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-bottom: clamp(24px, 4vh, 36px);
          font-weight: 500;
        }

        .svc-readmore-btn {
          font-family: 'Inter', sans-serif;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: clamp(10px, 2vw, 14px) clamp(24px, 4vw, 36px);
          background: #f97316;
          color: #ffffff;
          font-weight: 600;
          font-size: clamp(13px, 1.8vw, 15px);
          border-radius: 50px;
          cursor: pointer;
          border: none;
          transition: background 0.2s ease;
        }

        .svc-readmore-btn:hover { background: #ea580c; }

        @media (max-width: 600px) {
          .svc-cards-conveyor { padding: 0 16px; }
          .svc-card-front, .svc-card-back { border-radius: 16px; }
        }
      `}</style>

      <div className="svc-section" ref={sectionRef}>
        <div className="svc-bg-text-container">
          <h1 className="svc-bg-text" ref={textRef}>Services</h1>
        </div>

        <div className="svc-cards-conveyor" ref={cardsContainerRef}>
          {services.map((service) => (
            <div key={service.id} className="svc-card-wrapper">
              <div className="svc-card-inner">
                <div className="svc-card-front">
                  <div
                    className="svc-card-bg"
                    style={{ backgroundImage: `url(${service.image})` }}
                  />
                  <div className="svc-card-glass" />
                  <div className="svc-card-content">
                    <div className="svc-card-icon-wrapper">
                      <img
                        src={service.icon}
                        alt={service.name}
                        className="svc-card-icon"
                        width={56}
                        height={56}
                        loading="lazy"
                        decoding="async"
                      />
                      <h3 className="svc-card-name">{service.name}</h3>
                    </div>
                    <div className="svc-card-divider" />
                    <span className="svc-card-tagline">{service.tagline}</span>
                    <p className="svc-card-desc">{service.description}</p>
                  </div>
                </div>

                <div className="svc-card-back">
                  <h3 className="svc-back-title">{service.name}</h3>
                  <p className="svc-back-tagline">{service.tagline}</p>
                  <button
                    onClick={() => handleReadMore(service.slug)}
                    className="svc-readmore-btn"
                    type="button"
                  >
                    Read More
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}